// src/components/divesiteform/hooks/useDivesiteForm.js
import { useState, useEffect, useCallback } from "react";
import { DEFAULT_MAP_CENTER } from "../constants";
import useGeocode from "./useGeocode"; // Corrected import to match the hook's new behavior

console.log("useDivesiteForm HOOK CALLED");

const initialFormState = {
  name: "",
  location: "", // Full formatted address from geocoding
  city: "",
  province: "",
  province_short: "",
  country: "",
  country_short: "",
  divesitePara: "",
  diveDescription: "",
  environment: "",
  platform: "",
  depthRange: [5, 16], // Default depth range in meters
  surfaceSwimDistance: 0, // Default distance in meters
  mapCenter: DEFAULT_MAP_CENTER, // For controlling the map's initial view
  markerPosition: DEFAULT_MAP_CENTER, // For the draggable marker
  mediaImages: [], // Initialize as an empty array for multiple images
  heroImage: { file: null, description: "" }, // Object for file and description
  diagramImage: { file: null, description: "" }, // Object for file and description
  closestTideStation: "", // This will be set during submission
  slog: 0, // Assuming a default value for slog
  photosUploadedAndAgreed: false, // Default for the copyright checkbox
};

const useDivesiteForm = (isLoaded, showSnackbar) => {
  const [formState, setFormState] = useState(initialFormState);
  const [isLocating, setIsLocating] = useState(false);
  const [geolocationAttempted, setGeolocationAttempted] = useState(false); // To track if geolocation has been tried
  const { reverseGeocode } = useGeocode(showSnackbar); // useGeocode now only expects onError (showSnackbar)

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleMarkerDragEnd = useCallback(
    async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setFormState((prev) => ({
        ...prev,
        markerPosition: { lat, lng },
      }));

      // Call reverseGeocode and then update formState with its returned data
      const locationData = await reverseGeocode(lat, lng);
      if (locationData) {
        setFormState((prev) => ({
          ...prev,
          ...locationData, // Spread the locationData (city, province, country) directly into formState
          // You might also want to set a full 'location' string here if you have one
          // location: `${locationData.city}, ${locationData.province}, ${locationData.country}`,
        }));
      }
    },
    [reverseGeocode]
  );

  // Handler for single image file uploads (heroImage, diagramImage)
  const handleImageChange = useCallback(
    (imageType) => (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setFormState((prev) => ({
        ...prev,
        [imageType]: {
          ...prev[imageType], // Keep existing description if any
          file: file,
        },
      }));
    },
    []
  );

  // Handler for updating descriptions of single images (heroImage, diagramImage)
  const handleImageDescriptionChange = useCallback(
    (imageType) => (e) => {
      const { value } = e.target;
      setFormState((prev) => ({
        ...prev,
        [imageType]: {
          ...prev[imageType],
          description: value,
        },
      }));
    },
    []
  );

  // Handler for updating the array of multiple media images
  const handleMediaImagesChange = useCallback((newImagesArray) => {
    setFormState((prev) => ({
      ...prev,
      mediaImages: newImagesArray,
    }));
  }, []);

  // Handler for updating descriptions of individual media images within the array
  const handleMediaImageDescriptionChange = useCallback((index, value) => {
    setFormState((prev) => {
      const updatedMediaImages = [...prev.mediaImages];
      if (updatedMediaImages[index]) {
        updatedMediaImages[index] = {
          ...updatedMediaImages[index],
          description: value,
        };
      }
      return { ...prev, mediaImages: updatedMediaImages };
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setIsLocating(false); // Reset loading state
    setGeolocationAttempted(false); // Reset this so it can attempt again
  }, []);

  const handleGetLocation = useCallback(
    (onSuccess, onError) => {
      if (!isLoaded) {
        showSnackbar("Google Maps API not loaded yet.", "warning");
        onError({ code: 0, message: "Google Maps API not loaded yet." });
        return;
      }

      if (geolocationAttempted) {
        // If already attempted, don't re-attempt automatically
        return;
      }

      setIsLocating(true); // Start loading state
      setGeolocationAttempted(true); // Mark as attempted

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setFormState((prev) => ({
              ...prev,
              mapCenter: { lat: latitude, lng: longitude },
              markerPosition: { lat: latitude, lng: longitude },
            }));
            const locationData = await reverseGeocode(latitude, longitude); // Get data from hook
            if (locationData) {
              setFormState((prev) => ({
                ...prev,
                ...locationData, // Spread the locationData directly into formState
              }));
            }
            setIsLocating(false); // Stop loading state
            onSuccess();
          },
          (error) => {
            console.error("Geolocation error:", error);
            setIsLocating(false); // Stop loading state

            const messages = {
              1: "Location access denied. Please enable location services for this site in your browser settings.",
              2: "Unable to determine your location. Please check your internet connection or try again.",
              3: "Location request timed out. Please try again.",
            };
            const errorMessage =
              messages[error.code] ||
              "An unknown error occurred while getting your location.";
            showSnackbar(errorMessage, "error"); // Show specific error to user
            onError(error);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // Options for geolocation
        );
      } else {
        showSnackbar("Geolocation is not supported by your browser.", "error");
        setIsLocating(false);
        onError({ code: 0, message: "Geolocation not supported." });
      }
    },
    [isLoaded, geolocationAttempted, reverseGeocode, showSnackbar]
  );

  // Effect to attempt geolocation on initial load if not already attempted
  useEffect(() => {
    if (isLoaded && !geolocationAttempted) {
      handleGetLocation(
        () => {
          // Success callback: You might log something or do nothing specific here.
        },
        (error) => {
          // Error callback: `showSnackbar` is already called inside `handleGetLocation`
        }
      );
    }
  }, [isLoaded, geolocationAttempted, handleGetLocation]);

  return {
    formState,
    setFormState, // Exposed setFormState for other steps to directly update state
    handleInputChange,
    handleMarkerDragEnd,
    handleImageChange, // For single image file uploads
    handleImageDescriptionChange, // For single image descriptions
    handleMediaImagesChange, // For multiple image array updates
    handleMediaImageDescriptionChange, // For multiple image descriptions
    resetForm,
    handleGetLocation,
    isLocating,
    setGeolocationAttempted,
  };
};

export default useDivesiteForm;
